// MyAccountV2 — UI padrão global. Redesenho visual de "Minha Conta"
// (cabeçalho, Próximos, Histórico, Meu Perfil, estados vazios).
// NÃO contém nenhuma lógica nova de negócio: toda ação (cancelar, reagendar,
// avaliar, salvar perfil, logout) é recebida via props e executada pelas
// MESMAS funções já existentes em MyBookingsPage.tsx. Os diálogos de
// cancelamento/reagendamento/avaliação continuam sendo os originais,
// renderizados pelo componente pai — este arquivo só troca a lista e o shell.
//
// Cores: a cor principal (accentColor) vem de company.settings.brand_color
// (a MESMA fonte usada pela BookingPage pública). O modo claro/escuro segue
// company.settings.public_dark_mode (ou ?preview_dark=1). O texto sobre a cor
// da empresa é calculado por contraste (branco ou preto), nunca fixo.
import React from 'react';
import {
  Box, Typography, Avatar, Button, CircularProgress, Paper, IconButton,
  Alert, Chip, TextField,
} from '@mui/material';
import {
  ArrowBack, CalendarMonth, ContentCut, Refresh, Edit, Close,
  Star, StarBorder, CameraAlt, Add, EventBusy,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Texto legível (branco/preto) sobre uma cor sólida — luminância relativa.
function onColor(hex?: string): string {
  if (!hex || hex[0] !== '#') return '#fff';
  let h = hex.slice(1);
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length !== 6) return '#fff';
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  const L = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return L > 0.6 ? '#111111' : '#ffffff';
}

// Tokens de tema (claro/escuro) — espelham o DARK_THEME da BookingPage.
function palette(isDark: boolean) {
  return isDark
    ? { bg: '#0f0f0f', surface: '#1a1a1a', surfaceAlt: '#222222', border: '#2e2e2e',
        headerBorder: '#2e2e2e', tPrimary: '#f1f5f9', tStrong: '#f1f5f9', tSecondary: '#94a3b8',
        tMuted: '#64748b', divider: '#242424', tabTrack: '#1e1e1e' }
    : { bg: '#f7f8fa', surface: '#ffffff', surfaceAlt: '#f5f5f5', border: '#ececec',
        headerBorder: '#eeeeee', tPrimary: '#111111', tStrong: '#1a1a1a', tSecondary: '#666666',
        tMuted: '#999999', divider: '#f4f4f4', tabTrack: '#eef0f3' };
}

// Adendo 5.4.3 — Item 2: dias restantes até o vencimento, timezone da
// empresa (America/Sao_Paulo), nunca o fuso do aparelho do cliente nem
// new Date().toISOString() puro (sempre UTC — erraria o dia entre 21h e
// 23h59 horário de Brasília). 0 = vence hoje, 1 = vence amanhã, etc.
function getPlanDaysLeft(endDate?: string): number | null {
  if (!endDate) return null;
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date());
  const diffMs = new Date(`${endDate}T00:00:00`).getTime() - new Date(`${today}T00:00:00`).getTime();
  if (Number.isNaN(diffMs)) return null;
  return Math.round(diffMs / 86400000);
}

// Estado visual por prazo — Item 2, verbatim: >7 dias neutro; ≤7 dias
// alerta; vence hoje; vencido nunca aparece aqui (GET .../customers/
// subscriptions já só devolve end_date >= hoje).
function getPlanUrgency(daysLeft: number | null): { label: string; warn: boolean } {
  if (daysLeft === null) return { label: '', warn: false };
  if (daysLeft <= 0) return { label: 'Vence hoje', warn: true };
  if (daysLeft <= 7) return { label: `Faltam ${daysLeft} dia${daysLeft === 1 ? '' : 's'}`, warn: true };
  return { label: `Faltam ${daysLeft} dias`, warn: false };
}

function getPlanRemaining(item: any): number {
  return Math.max(0, Number(item?.quantity_total || 0) - Number(item?.quantity_used || 0));
}

// Adendo 5.4.3.2 — decisão de produto (após incidente do agrupamento em
// BookingPage.tsx, mesma causa evitada aqui por precaução): cada instância
// de pacote é mostrada SEPARADAMENTE, sem mesclar visualmente duas
// ativações do mesmo plano. Sem soma nem prorrogação automática de
// validade — fica para uma regra própria de renovação, futura.
// (groupActivePlans foi removida; se agrupamento voltar, nunca usar
// `new Map` num arquivo que possa vir a importar um ícone chamado `Map`.)

const STATUS_MAP: any = {
  confirmed: { label: 'Confirmado', color: '#2e7d32', bg: '#e8f5e9' },
  pending: { label: 'Pendente', color: '#e65100', bg: '#fff3e0' },
  completed: { label: 'Concluído', color: '#1565c0', bg: '#e3f2fd' },
  cancelled: { label: 'Cancelado', color: '#b71c1c', bg: '#ffebee' },
  no_show: { label: 'Não compareceu', color: '#555', bg: '#f5f5f5' },
};

function EmptyState({ upcoming, onBook, C, accent, onAccent }: any) {
  return (
    <Box sx={{ py: 6, textAlign: 'center', px: 3 }}>
      <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: C.surfaceAlt, display: 'flex',
        alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
        {upcoming ? <CalendarMonth sx={{ fontSize: 28, color: C.tMuted }} /> : <EventBusy sx={{ fontSize: 28, color: C.tMuted }} />}
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: C.tPrimary, mb: 0.5 }}>
        {upcoming ? 'Nenhum agendamento futuro' : 'Nenhum histórico ainda'}
      </Typography>
      <Typography sx={{ fontSize: 13, color: C.tMuted, mb: 2.5 }}>
        {upcoming ? 'Que tal marcar seu próximo horário?' : 'Seus atendimentos concluídos aparecem aqui.'}
      </Typography>
      {upcoming && (
        <Button variant="contained" startIcon={<Add sx={{ fontSize: 18 }} />} onClick={onBook}
          sx={{ bgcolor: accent, color: onAccent, '&:hover': { bgcolor: accent, filter: 'brightness(0.92)' },
            borderRadius: 2.5, textTransform: 'none', fontWeight: 700, px: 3, minHeight: 44, boxShadow: 'none' }}>
          Agendar horário
        </Button>
      )}
    </Box>
  );
}

function BookingCard({ b, isHistorical, onReschedule, onCancel, onReview, onDetail, onRepeat, C, accent, isDark }: any) {
  const status = STATUS_MAP[b.status] || STATUS_MAP.pending;
  const dateObj = parseISO(b.date + 'T12:00:00');
  const services = b.booking_services && b.booking_services.length > 0
    ? b.booking_services.map((bs: any) => bs.service?.name).filter(Boolean).join(' + ')
    : b.service?.name;
  // Adendo 5.4.3 — Item 1: package_usage só vem preenchido quando o
  // agendamento realmente consumiu saldo de pacote (ver myBookings,
  // CustomerAuthController.ts) — nunca inferido no front.
  const pkg = b.package_usage;

  return (
    <Paper sx={{ mb: 1.5, borderRadius: 3, border: `1px solid ${C.border}`, overflow: 'hidden',
      bgcolor: C.surface, boxShadow: isDark ? 'none' : '0 1px 6px rgba(0,0,0,0.04)' }}>
      <Box sx={{ px: 2, py: 1.6, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 1.4, minWidth: 0 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: C.surfaceAlt, display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
            {b.service?.image_url ? <img src={b.service.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <ContentCut sx={{ fontSize: 18, color: C.tMuted }} />}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontWeight: 800, fontSize: 14.5, color: C.tPrimary }}>
              {format(dateObj, "dd 'de' MMM", { locale: ptBR })} · {b.start_time?.slice(0, 5)}
            </Typography>
            <Typography noWrap sx={{ fontSize: 12.5, color: C.tSecondary, mt: 0.2 }}>{services || 'Serviço'}</Typography>
            {b.professional?.name && (
              <Typography noWrap sx={{ fontSize: 11.5, color: C.tMuted }}>{b.professional.name}</Typography>
            )}
            {pkg && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.4 }}>
                <Chip label="Pacote" size="small" sx={{ bgcolor: `${accent}1f`, color: accent, fontWeight: 800, height: 18, fontSize: 9.5, '& .MuiChip-label': { px: 0.8 } }} />
                <Typography noWrap sx={{ fontSize: 11, color: C.tMuted }}>
                  {pkg.plan_name}{pkg.total_quantity > 1 ? ` · ${pkg.total_quantity} serviços do pacote` : ''}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        <Chip label={status.label} size="small" sx={{ bgcolor: status.bg, color: status.color, fontWeight: 700, fontSize: 10.5, flexShrink: 0 }} />
      </Box>

      {/* Avaliação: já feita ou disponível */}
      {isHistorical && b.review && (
        <Box sx={{ px: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }} onClick={() => onDetail(b)}>
          <Star sx={{ fontSize: 14, color: '#f59e0b' }} />
          <Typography sx={{ fontSize: 12, color: '#f59e0b', fontWeight: 700 }}>Você deu nota {b.review.rating}</Typography>
        </Box>
      )}
      {b.status === 'completed' && !b.review && (
        <Box sx={{ px: 2, pb: 1.4 }}>
          <Button size="small" startIcon={<StarBorder sx={{ fontSize: 15 }} />} onClick={() => onReview(b)}
            sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12, color: '#f59e0b', px: 0 }}>
            Avaliar atendimento
          </Button>
        </Box>
      )}

      {/* Ações — só o que o backend realmente permite */}
      {isHistorical ? (
        <Box sx={{ borderTop: `1px solid ${C.divider}`, px: 2, py: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Button size="small" startIcon={<Refresh sx={{ fontSize: 13 }} />} onClick={() => onRepeat(b)}
            sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12, color: C.tSecondary, minHeight: 36 }}>
            Repetir
          </Button>
        </Box>
      ) : b.company?.allow_customer_cancel !== false ? (
        <Box sx={{ borderTop: `1px solid ${C.divider}`, px: 2, py: 1, display: 'flex', gap: 1 }}>
          <Button size="small" fullWidth startIcon={<Edit sx={{ fontSize: 14 }} />} onClick={() => onReschedule(b)}
            sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12.5, bgcolor: `${accent}1f`, color: accent, borderRadius: 2, minHeight: 40, '&:hover': { bgcolor: `${accent}30` } }}>
            Reagendar
          </Button>
          <Button size="small" fullWidth startIcon={<Close sx={{ fontSize: 14 }} />} onClick={() => onCancel(b)}
            sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12.5, bgcolor: isDark ? 'rgba(229,57,53,0.16)' : '#fef1f1', color: isDark ? '#ff6b6b' : '#e53935', borderRadius: 2, minHeight: 40, '&:hover': { bgcolor: isDark ? 'rgba(229,57,53,0.26)' : '#fde2e2' } }}>
            Cancelar
          </Button>
        </Box>
      ) : null}
    </Paper>
  );
}

export function MyAccountV2(props: any) {
  const {
    custUser, tab, setTab, upcoming, past, list, isHistorical: isHistoricalFn,
    loading, error, tokenExpired, activeSubscriptions, subscriptionsLoading,
    openReschedule, openCancel, openReview, openDetail, onRepeat, onBookNew, onUseSaldo,
    // perfil
    profileName, setProfileName, profilePhone, setProfilePhone, profileEmail, setProfileEmail,
    profileBirthday, setProfileBirthday, profileAddress, setProfileAddress, profilePhoto,
    profilePwd, setProfilePwd, profilePwd2, setProfilePwd2,
    profileSaving, profileSuccess, profileError, handleSaveProfile, handlePhotoChange,
    onLogout,
    // tema (vem do MyBookingsPage, mesma fonte da BookingPage pública)
    accentColor, isDark,
  } = props;

  const accent = accentColor || '#c9a227';
  const onAccent = onColor(accent);
  const dark = isDark === true;
  const C = palette(dark);
  const nextBooking = upcoming?.[0];

  // Botão primário na cor da empresa (com texto por contraste)
  const primaryBtn = {
    bgcolor: accent, color: onAccent, boxShadow: 'none',
    '&:hover': { bgcolor: accent, filter: 'brightness(0.92)' },
    borderRadius: 2.5, textTransform: 'none' as const,
  };
  // Campos com foco na cor da empresa (+ dark mode legível)
  const fieldSx: any = {
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: accent },
    '& .MuiInputLabel-root.Mui-focused': { color: accent },
    ...(dark ? {
      '& .MuiOutlinedInput-root': { color: C.tPrimary, backgroundColor: C.surfaceAlt },
      '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border },
      '& .MuiInputLabel-root': { color: C.tSecondary },
      '& .MuiSvgIcon-root': { color: C.tSecondary },
    } : {}),
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: C.bg, pb: 'env(safe-area-inset-bottom, 16px)' }}>
      {/* Cabeçalho compacto */}
      <Box sx={{ bgcolor: C.surface, borderBottom: `1px solid ${C.headerBorder}`, px: 2, pt: 'env(safe-area-inset-top, 0px)' }}>
        <Box sx={{ maxWidth: 560, mx: 'auto', display: 'flex', alignItems: 'center', gap: 1.2, py: 1.2, minHeight: 44 }}>
          <IconButton size="small" onClick={() => window.history.back()} sx={{ color: C.tSecondary, minWidth: 44, minHeight: 44 }} aria-label="Voltar">
            <ArrowBack fontSize="small" />
          </IconButton>
          <Avatar src={custUser.photo_url} sx={{ width: 32, height: 32, bgcolor: accent, color: onAccent, fontSize: 14, fontWeight: 700 }}>
            {custUser.name?.[0]}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography noWrap sx={{ fontWeight: 800, fontSize: 14.5, color: C.tStrong }}>{custUser.name}</Typography>
            {upcoming?.[0]?.company?.name && (
              <Typography noWrap sx={{ fontSize: 11, color: C.tMuted }}>{upcoming[0].company.name}</Typography>
            )}
          </Box>
          <Button size="small" onClick={onLogout}
            sx={{ textTransform: 'none', fontSize: 12, fontWeight: 600, color: C.tMuted, minHeight: 44, minWidth: 44 }}>
            Sair
          </Button>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 560, mx: 'auto', px: 2, pt: 2 }}>
        {/* Saudação + CTA */}
        {tab !== 'profile' && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 13, color: C.tSecondary, mb: 1.2 }}>
              Olá, {custUser.name?.split(' ')[0]} 👋
            </Typography>
            <Button fullWidth variant="contained" startIcon={<Add />} onClick={onBookNew}
              sx={{ ...primaryBtn, fontWeight: 800, fontSize: 14.5, py: 1.3, minHeight: 48 }}>
              Agendar novo horário
            </Button>
          </Box>
        )}

        {/* Resumo do próximo agendamento — cartão NEUTRO; a cor da empresa entra
            só como detalhe (barra lateral + ícone + data), sem preencher o cartão. */}
        {tab !== 'profile' && nextBooking && (
          <Paper sx={{ mb: 2, borderRadius: 3, p: 2, bgcolor: C.surface, border: `1px solid ${C.border}`,
            borderLeft: `3px solid ${accent}`, boxShadow: 'none', display: 'flex', gap: 1.3, alignItems: 'flex-start' }}>
            <Box sx={{ width: 34, height: 34, borderRadius: 2, bgcolor: `${accent}1f`, display: 'flex',
              alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.2 }}>
              <CalendarMonth sx={{ fontSize: 18, color: accent }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 11, color: C.tMuted, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.3 }}>
                Seu próximo horário
              </Typography>
              <Typography sx={{ fontWeight: 800, fontSize: 15.5, color: accent }}>
                {format(parseISO(nextBooking.date + 'T12:00:00'), "EEE, dd 'de' MMM", { locale: ptBR })} às {nextBooking.start_time?.slice(0, 5)}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: C.tSecondary, mt: 0.2 }}>
                {nextBooking.service?.name || nextBooking.booking_services?.[0]?.service?.name || 'Serviço'}
              </Typography>
            </Box>
          </Paper>
        )}

        {/* Tabs */}
        <Box sx={{ display: 'flex', gap: 0.5, mb: 2, bgcolor: C.tabTrack, borderRadius: 2.5, p: 0.4 }}>
          {[
            { key: 'upcoming', label: `Próximos (${upcoming.length})` },
            { key: 'past', label: `Histórico (${past.length})` },
            { key: 'profile', label: 'Meu Perfil' },
          ].map(t => {
            const activeTab = tab === t.key;
            return (
              <Box key={t.key} onClick={() => setTab(t.key)}
                sx={{ flex: 1, textAlign: 'center', py: 1, borderRadius: 2, cursor: 'pointer', minHeight: 40,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: activeTab ? accent : 'transparent',
                  boxShadow: activeTab ? '0 1px 4px rgba(0,0,0,0.15)' : 'none',
                  transition: 'all 0.15s' }}>
                <Typography sx={{ fontWeight: activeTab ? 800 : 500, fontSize: 12.5, color: activeTab ? onAccent : C.tSecondary }}>
                  {t.label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Adendo 5.4.3 — Item 2: resumo real do saldo em pacotes (nome, saldo
            por serviço, validade, "Faltam X dias", cor por prazo, "Agendar
            com meu saldo"). Adendo 5.4.3.2 — decisão de produto: cada
            instância separada, sem agrupar visualmente duas ativações do
            mesmo plano (ver nota no topo do arquivo sobre a causa do
            incidente de 30/08). */}
        {(tab === 'upcoming' || tab === 'past') && (
          subscriptionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}><CircularProgress size={22} sx={{ color: accent }} /></Box>
          ) : activeSubscriptions.length > 0 ? (
            <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
              {activeSubscriptions.map((row: any) => {
                const remainingItems = (Array.isArray(row.items) ? row.items : []).filter((it: any) => getPlanRemaining(it) > 0);
                const daysLeft = getPlanDaysLeft(row.end_date);
                const urgency = getPlanUrgency(daysLeft);
                const urgencyColor = urgency.warn ? (dark ? '#fbbf24' : '#b45309') : C.tSecondary;
                const planTitle = row.subscription_plan?.public_title || row.plan_name || 'Pacote';
                return (
                  <Paper key={row.id} sx={{ borderRadius: 3, border: `1px solid ${accent}44`, p: 2, bgcolor: `${accent}0f`, boxShadow: 'none' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.6 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: 13.5, color: C.tStrong }}>{planTitle}</Typography>
                        <Typography sx={{ fontSize: 12, color: urgencyColor, fontWeight: urgency.warn ? 800 : 400, mt: 0.2 }}>
                          {urgency.label}
                        </Typography>
                      </Box>
                    </Box>

                    {remainingItems.length > 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, mb: 1 }}>
                        {remainingItems.map((item: any) => (
                          <Typography key={item.id} sx={{ fontSize: 12.4, color: C.tSecondary }}>
                            <Box component="span" sx={{ fontWeight: 900, color: C.tPrimary }}>{getPlanRemaining(item)}x</Box>
                            {' '}{item.service?.name || item.service_name}
                          </Typography>
                        ))}
                      </Box>
                    )}

                    <Button fullWidth variant="contained" onClick={onUseSaldo}
                      sx={{ bgcolor: accent, color: onAccent, '&:hover': { bgcolor: accent, filter: 'brightness(0.92)' }, borderRadius: 2, textTransform: 'none', fontWeight: 800, boxShadow: 'none' }}>
                      Agendar com meu saldo
                    </Button>
                  </Paper>
                );
              })}
            </Box>
          ) : null
        )}

        {/* Próximos / Histórico */}
        {(tab === 'upcoming' || tab === 'past') && (
          loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: accent }} /></Box>
          : error ? <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
          : tokenExpired ? (
            <Box sx={{ py: 5, textAlign: 'center' }}>
              <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1, color: C.tPrimary }}>Sessão expirada</Typography>
              <Typography sx={{ fontSize: 12.5, color: C.tMuted }}>Volte pela página de agendamento para continuar.</Typography>
            </Box>
          )
          : list.length === 0 ? <EmptyState upcoming={tab === 'upcoming'} onBook={onBookNew} C={C} accent={accent} onAccent={onAccent} />
          : list.map((b: any) => (
            <BookingCard key={b.id} b={b} isHistorical={isHistoricalFn(b)}
              onReschedule={openReschedule} onCancel={openCancel} onReview={openReview}
              onDetail={openDetail} onRepeat={onRepeat} C={C} accent={accent} isDark={dark} />
          ))
        )}

        {/* Meu Perfil */}
        {tab === 'profile' && (
          <Box sx={{ pb: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
              <Box sx={{ position: 'relative', mb: 1.2 }}>
                <Avatar src={profilePhoto || custUser?.photo_url} sx={{ width: 80, height: 80, fontSize: 28, fontWeight: 700, bgcolor: accent, color: onAccent, border: `3px solid ${C.surface}`, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
                  {(profileName || custUser?.name || 'C')[0]?.toUpperCase()}
                </Avatar>
                <Box component="label" sx={{ position: 'absolute', bottom: 0, right: 0, width: 28, height: 28, borderRadius: '50%',
                  bgcolor: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
                  <CameraAlt sx={{ fontSize: 14, color: onAccent }} />
                  <input type="file" accept="image/*" hidden onChange={handlePhotoChange} />
                </Box>
              </Box>
              <Typography sx={{ fontWeight: 800, fontSize: 16, color: C.tPrimary }}>{profileName || custUser?.name}</Typography>
            </Box>

            {profileSuccess && <Alert severity="success" sx={{ mb: 1.5, borderRadius: 2 }}>{profileSuccess}</Alert>}
            {profileError && <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }}>{profileError}</Alert>}

            <Paper sx={{ borderRadius: 3, border: `1px solid ${C.border}`, p: 2, mb: 1.5, display: 'flex', flexDirection: 'column', gap: 1.6, bgcolor: C.surface, boxShadow: 'none' }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: C.tMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Dados pessoais</Typography>
              <TextField label="Nome completo" value={profileName} onChange={(e: any) => setProfileName(e.target.value)} fullWidth size="small" sx={fieldSx} />
              <TextField label="WhatsApp" value={profilePhone} onChange={(e: any) => setProfilePhone(e.target.value)} fullWidth size="small" placeholder="(11) 99999-9999" sx={fieldSx} />
              <TextField label="E-mail" value={profileEmail} onChange={(e: any) => setProfileEmail(e.target.value)} fullWidth size="small" type="email" sx={fieldSx} />
              <TextField label="Data de nascimento" value={profileBirthday} onChange={(e: any) => setProfileBirthday(e.target.value)} fullWidth size="small" type="date" slotProps={{ inputLabel: { shrink: true } }} sx={fieldSx} />
              <TextField label="Endereço" value={profileAddress} onChange={(e: any) => setProfileAddress(e.target.value)} fullWidth size="small" sx={fieldSx} />
            </Paper>

            <Paper sx={{ borderRadius: 3, border: `1px solid ${C.border}`, p: 2, mb: 2, display: 'flex', flexDirection: 'column', gap: 1.6, bgcolor: C.surface, boxShadow: 'none' }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: C.tMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Alterar senha</Typography>
              <TextField label="Nova senha" value={profilePwd} onChange={(e: any) => setProfilePwd(e.target.value)} fullWidth size="small" type="password" sx={fieldSx} />
              <TextField label="Confirmar nova senha" value={profilePwd2} onChange={(e: any) => setProfilePwd2(e.target.value)} fullWidth size="small" type="password" sx={fieldSx} />
            </Paper>

            <Button fullWidth variant="contained" disabled={profileSaving} onClick={handleSaveProfile}
              sx={{ ...primaryBtn, fontWeight: 800, py: 1.4, minHeight: 48 }}>
              {profileSaving ? 'Salvando...' : 'Salvar alterações'}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
